


import {Component, Prop, Watch, Vue, Ref} from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import _cloneDeep from 'lodash.clonedeep';
import _isEqual from 'lodash.isequal';
import { email, maxLength, required, url } from 'vuelidate/lib/validators';
import uploadImage from '@/views/components/uploadImages/uploadImage.vue';
import uploadFiles from '@/views/components/uploadFiles/uploadFiles.vue';
import IconCloseQrPopup from '@/_modules/icons/components/qrpopup/icon-close-qr-popup.vue';
import TranslitHelper from '@/_helpers/translit.helper';
import {TContactTag} from '@/_types/contact-tag.type';
import {TEditContactActionParams} from '@/_modules/promo/store/promo-page.store';
import {TLeaveFunction} from '@/_modules/promo/types/edit-form-store-state.type';
import {Validations} from 'vuelidate-property-decorators';
import {TVuelidateRuleSet} from '@/_types/vuelitation-rule-set.type';
import {TContact} from '@/_types/contact.type';
import {TCountry} from '@/_types/country.type';
import {TUser} from '@/_types/user.type';
import {TEvent} from '@/_types/event.type';
import {TProduct} from '@/_types/promo-page/product.type';
import {TPromoPage} from '@/_types/promo-page/promo-page.type';
import {TFile} from '@/_types/file.type';

const translitHelper = new TranslitHelper();

const CONTACT_TAG_SELECTION_LIMIT = 10;

interface IUserPhotoUploader {
  showExternalImage: (photoUrl: string) => void;
}

@Component({
  components: {
    uploadImage,
    uploadFiles,
    IconCloseQrPopup,
  },
})
export default class EditContact extends Vue {

  @Ref('standalone-contact-textarea') standaloneContactTextarea: HTMLTextAreaElement;

  @Getter('promoStore/promoOwnerId') promoOwnerId: number;
  @Getter('promoStore/myPromoPage') myPromoPage: TPromoPage;
  @Getter('promoStore/editPromoPageSuccess') editPromoPageSuccess: boolean;
  @Getter('promoStore/editPromoPageLoading') editPromoPageLoading: boolean;
  @Getter('promoStore/productsListStandalone') productListStandalone: TProduct[];
  @Getter('_userStore/user') public userInfo: TUser;
  @Getter('editFormStore/hasUnsavedChanges') public hasUnsavedChanges: boolean;
  @Getter('promoPageStore/contact') public contactInfo: TContact;
  @Getter('promoPageStore/isContactLoading') public isContactLoading: boolean;
  @Getter('_eventStore/event') public event: TEvent;
  @Getter('_eventStore/eventContactTags') public eventContactTags: TContactTag[];

  @Action('editFormStore/setHasUnsavedChanges') public setHasUnsavedChanges: (newVal: boolean) => void;
  @Action('editFormStore/showConfirmLeavePopup') public showConfirmLeavePopup: (leaveFunction?: TLeaveFunction) => void;
  @Action('editFormStore/hideConfirmLeavePopup') public hideConfirmLeavePopup: () => void;
  @Action('editFormStore/leave') public leave: () => void;
  @Action('promoPageStore/editContact') public editContact: (params: TEditContactActionParams) => Promise<boolean>;

  @Action('locationStore/requestCountries') public requestLocationStoreCountries: () => Promise<TCountry[]>;
  @Action('promoPageStore/getContact') public getContact: (payload: { eventId: number; acceptLanguage: string }) => Promise<void>;
  @Action('promoPageStore/setContactPhotoURL') setContactPhotoURL: (newImageUrl: string) => Promise<void>;

  @Prop({
    type: Array,
    default: (): string[] => {
      return [];
    }
  })
  private eventLanguages: string[];

  public standaloneContactUrlCopySuccessMessage: string = '';
  public errors: {[key: string]: string } = {
    website: ''
  };
  public isLoading: boolean = false;
  public isContactLoadingWatcherBlocked: boolean = true;
  public selectedLanguageTab: string = '';
  public isEditSuccessMessageShown: boolean = false;
  public contactId: number = -1; // TODO: is it needed?
  public contact: TContact = {
    id: 0,
    event_id: 0,
    name: '',
    surname: '',
    email: '',
    photo_url: '',
    country: '',
    city: '',
    company_name: '',
    company_website: '',
    company_position: '',
    company_description: '',
    files: [],
    tags: [],
    phone: '',
  };
  public oldContact: TContact = { // Needed to compare with in watch/contact
    id: 0,
    event_id: 0,
    name: '',
    surname: '',
    email: '',
    photo_url: '',
    country: '',
    city: '',
    company_name: '',
    company_website: '',
    company_position: '',
    company_description: '',
    files: [],
    tags: [],
    phone: '',
  };
  public isContactTagSelectorVisible: boolean = false;
  public eventContactTagsLocal: TContactTag[] = [];
  public copyStandaloneContactTimeOut: number = 0;

  public set contactCompanyWebsite(url: string) {
    this.contact.company_website = url === 'https://' ? '' : url;
  }

  public get contactCompanyWebsite(): string {
    return this.contact.company_website || 'https://';
  }

  public get isContactTagSelectionLimitReached(): boolean {
    const selectedTagsQuantity = this.eventContactTagsLocal.filter(item => (item as any).isSelected).length;
    return CONTACT_TAG_SELECTION_LIMIT <= selectedTagsQuantity;
  }

  public get eventContactTagsLocalSelected(): TContactTag[] {
    // Not cloning because we do not mutate it in any way yet. Feel free to _cloneDeep if requirements change.
    return this.eventContactTagsLocal.filter(item => (item as any).isSelected);
  }

  public get currentEditLanguage(): string {
    if (this.eventLanguages && this.eventLanguages.length) {
      if (this.selectedLanguageTab) {
        return this.selectedLanguageTab;
      }
      return this.eventLanguages[0];
    }

    // TODO: is it ok? Maybe not.
    // When a user visits by URL, say, ru/events/365/promo/edit/company
    // they see the promopage data in Russian, but the selected tab is English
    // This can save bad data into the DB.
    // Because of this, I hardcoded a default language here so that
    // this.getPromopageContactId always has some language in this.currentEditLanguage
    return 'en';
  }

  public get isShareContactUrlVisible(): boolean {
    const hasOrigin = !!(document && document.location && document.location.origin);
    const isEventsWalletDomain = hasOrigin && document.location.origin.indexOf('eventswallet') >= 0;
    const isLocalDomain = hasOrigin && document.location.origin.indexOf('local') >= 0;
    return hasOrigin && (isEventsWalletDomain || isLocalDomain);
  }

  public get standaloneContactUrl(): string {
    if (!this.contact || !this.contactId || !this.eventId || !this.isShareContactUrlVisible) {
      return '';
    }
    const eventSlug = translitHelper.getTransliterated(this.event.title) + '-' + this.eventId.toFixed(0);
    return `${document.location.origin}/${this.$route.params.lang}/events/${eventSlug}/contact/${this.contact.id}`;
  }

  @Watch('userInfo', { deep: true, immediate: true })
  private onUserInfoChange(): void {
    this.callContactInfo();
  }

  @Watch('contactInfo', { deep: true })
  private onContactInfoChange(newVal: TContact): void {
    if (!newVal) {
      return;
    }
    this.setContactData();
    if (this.$refs.userPhotoUploader && this.contactInfo.photo_url) {
      (this.$refs.userPhotoUploader as unknown as IUserPhotoUploader).showExternalImage(this.contactInfo.photo_url);
    }
  }

  @Watch('contact', { deep: true })
  private onContactChange(): void {
    this.setHasUnsavedChanges(!_isEqual(this.contact, this.oldContact));
  }

  @Watch('eventContactTags')
  private onEventContactTagsChange(): void {
    let selectedTagIds: number[] = [];
    if (this.contact && this.contact.tags) {
      selectedTagIds = this.contact.tags.map(item => item.id);
    }
    this.eventContactTagsLocal = _cloneDeep(this.eventContactTags).map(item => {
      (item as any).isSelected = !!selectedTagIds.find(selectedItem => item.id === selectedItem);
      return item;
    });
  }

  @Watch('isContactLoading', { immediate: true })
  private onIsContactLoadingChange(newVal: boolean, oldVal: boolean): void {
    if (this.isContactLoadingWatcherBlocked) {
      return;
    }
    if (!newVal && oldVal) {
      this.refreshContactData();
      this.isContactLoadingWatcherBlocked = true;
    }
  }

  public get eventId(): number {
    return this.$route.params.eventId ? parseInt(this.$route.params.eventId, 10) : null;
  }

  public mounted(): void {
    this.contact.event_id = this.eventId; // see type TEditContactActionParams
    this.requestLocationStoreCountries();
    // TODO: refactor. maybe research the getContactPromoPageRequest.cancel() way — check side-bar-right, bad effects of cancel() can be there.
    if (this.isContactLoading) { // AW-2671. Could also be done by getContactPromoPageRequest.cancel() but that creates other problems.
      this.isContactLoadingWatcherBlocked = false;
    } else {
      this.refreshContactData();
    }
    this.getContactTags();
  }

  public async refreshContactData(): Promise<void> {
    await this.callContactInfo();
    this.setContactData();
  }

  public async callContactInfo(): Promise<void> {
    await this.getContact({
      eventId: this.eventId,
      acceptLanguage: this.currentEditLanguage
    });
  }

  public setContactData(): void {

    if (!this.contactInfo) return;

    this.contactId = this.contactInfo.id;

    const contactKeys = Object.keys(this.contact);
    contactKeys.forEach(contactKey => {
      if (this.contactInfo[(contactKey as keyof TContact)]) {
        // @ts-ignore TODO: remove ts-ignore
        this.contact[contactKey] = _cloneDeep(this.contactInfo[(contactKey as keyof TContact)]);
      } else if (typeof this.contact[(contactKey as keyof TContact)] === 'string') {
        // @ts-ignore TODO: remove ts-ignore
        this.contact[(contactKey as keyof TContact)] = '';
      }
    });

    this.oldContact = _cloneDeep(this.contact);

  }

  public handlePhotoUrl(payload: { image: string }): void {
    this.contact.photo_url = payload.image || '';
    this.setContactPhotoURL(payload.image || '');
  }

  public handleFiles(payload: TFile[]): void {
    this.contact.files = payload;
  }

  public phoneInputKeyupHandler(): void {
    this.contact.phone = '+' + this.contact.phone.replace(/[^\d]/g, '');
    if (this.contact.phone === '+') {
      this.contact.phone = '';
    }
  }

  public async onSaveClick(): Promise<void> {
    this.removeEditContactSuccessMessage();

    this.$v.contact.$touch();
    if (this.$v.contact.$pending || this.$v.contact.$invalid) {
      return;
    }

    this.isLoading = true;

    await this.saveContactDataAndDisplaySuccess();

    // TODO: check can we remove this call and rely only on Vuex reactivity?
    await this.getContact({ eventId: this.eventId, acceptLanguage: this.currentEditLanguage });

    this.saveContactDocuments();
    this.isLoading = false;
    this.setContactFormSaved();
  }

  public async saveContactDataAndDisplaySuccess(): Promise<void> {
    const editContactResponse = await this.$store.dispatch('promoPageStore/editContact', this.getPreparedContactDataToSend());
    if (editContactResponse) {
      this.showEditContactSuccessMessage();
    }
  }

  public getPreparedContactDataToSend(): TContact {
    const contactDataToSend = _cloneDeep(this.contact);
    delete contactDataToSend.files; // Files have to be sent separately since they are an array of objects
    delete contactDataToSend.id; // API PATCH contact/info does not accept the id request parameter
    contactDataToSend.tags = this.eventContactTagsLocalSelected;
    (contactDataToSend as any).lang = this.currentEditLanguage;
    return contactDataToSend;
  }

  public saveContactDocuments(): void {
    for (let i = 0; i < this.contact.files.length; i++) {

      const iteratedFile: any = (this.contact.files[i] as unknown) as any;
      // If the file has id or tempUploadedFlag, then it has already been sent, no need to re-send.
      if (iteratedFile.id || (iteratedFile.tempUploadedFlag || !iteratedFile.url)) {
        continue;
      }

      const fileInfoToSend = {
        url: iteratedFile.url,
        filename: iteratedFile.name,
        event_id: this.eventId
      };

      this.$store.dispatch('contactsStore/putContactFile', fileInfoToSend).then(() => {
        // Marking local file info object as sent with the «tempUploadedFlag»
        this.contact.files.splice(i, 1, {
          ...iteratedFile,
          tempUploadedFlag: true
        });
      });
    }
  }

  public setContactFormSaved(): void {
    this.oldContact = _cloneDeep(this.contact);
    this.setHasUnsavedChanges(false);
  }

  public showEditContactSuccessMessage(): void {
    this.isEditSuccessMessageShown = true;
    setTimeout(this.removeEditContactSuccessMessage, 9000);
  }

  public removeEditContactSuccessMessage(): void {
    this.isEditSuccessMessageShown = false;
  }

  public openContactTagsSelectorPopup(): void {
    this.isContactTagSelectorVisible = true;
  }

  public closeContactTagsSelectorPopup(): void {
    this.isContactTagSelectorVisible = false;
  }

  public handleContactTagClick(contactTag: TContactTag): void {
    if ((contactTag as any).isSelected === false && this.isContactTagSelectionLimitReached) {
      return;
    }
    (contactTag as any).isSelected = !(contactTag as any).isSelected;
  }

  public async getContactTags(): Promise<void> {
    await this.$store.dispatch('_eventStore/getEventContactTags');
  }

  public clearSelectedContactTags(): void {
    this.eventContactTagsLocal = this.eventContactTagsLocal.map((item) => {
      (item as any).isSelected = false;
      return { ...item };
    });
    this.closeContactTagsSelectorPopup();
  }

  public selectLanguageTab(langCode: string): void {
    const leaveAction: TLeaveFunction = (): void => {
      if (this.selectedLanguageTab !== langCode) {
        this.selectedLanguageTab = langCode;
        this.setHasUnsavedChanges(false);
        this.hideConfirmLeavePopup();
        this.$store.dispatch('promoPageStore/getContact', { eventId: this.eventId, acceptLanguage: this.currentEditLanguage });
      } else {
        this.selectedLanguageTab = langCode;
      }
    };

    if (this.selectedLanguageTab !== langCode && this.hasUnsavedChanges) {
      this.showConfirmLeavePopup(leaveAction);
    } else {
      leaveAction();
    }

  }

  public copyStandaloneContactUrl(): void {
    window.clearTimeout(this.copyStandaloneContactTimeOut);
    this.standaloneContactTextarea.select();
    if (document.execCommand('copy')) {
      this.standaloneContactUrlCopySuccessMessage = `${this.$t('promo.company.edit.standAloneCompanyCode.copySuccess')}`;
      this.copyStandaloneContactTimeOut = window.setTimeout(() => {
        this.standaloneContactUrlCopySuccessMessage = '';
      }, 3000);
    }
  }

  @Validations()
  public validations: TVuelidateRuleSet<any> = {
    contact: {
      name: {
        required,
      },
      surname: {
        required,
      },
      email: {
        maxLength: maxLength(250),
        email,
        required,
      },
      company_website: {
        maxLength: maxLength(250),
        url,
      },
      video_stream_url: { // TODO: is this used?
        maxLength: maxLength(250),
        url,
      },
    },
  }
}
